import React, { useEffect, useState } from 'react';
import { apiService } from '../services/api.service';

type Tab = 'holidays' | 'localities' | 'concepts' | 'referral' | 'occupations' | 'bureau' | 'publicEntities';

export function ReferenceData() {
  const [tab, setTab] = useState<Tab>('referral');
  const [holidays, setHolidays] = useState<any[]>([]);
  const [localities, setLocalities] = useState<any[]>([]);
  const [concepts, setConcepts] = useState<any[]>([]);
  const [referralSources, setReferralSources] = useState<any[]>([]);
  const [occupations, setOccupations] = useState<any[]>([]);
  const [bureauProviders, setBureauProviders] = useState<any[]>([]);
  const [publicEntities, setPublicEntities] = useState<any[]>([]);
  const [branches, setBranches] = useState<any[]>([]);
  const [entityBranchFilter, setEntityBranchFilter] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [holidayForm, setHolidayForm] = useState({ date: '', name: '' });
  const [localityForm, setLocalityForm] = useState({ name: '', province: '', postalCode: '' });
  const [conceptForm, setConceptForm] = useState({ code: '', name: '', description: '' });
  const [referralForm, setReferralForm] = useState({ code: '', name: '' });
  const [occupationForm, setOccupationForm] = useState({ code: '', name: '' });
  const [entityForm, setEntityForm] = useState({
    code: '',
    name: '',
    encoding: 'latin1',
    sendLayout: '',
    responseLayout: '',
    sendFileConfig: '',
    responseFileConfig: '',
    branchIds: [] as string[],
    primaryBranchId: '',
  });
  const [editingEntity, setEditingEntity] = useState<string | null>(null);

  const load = async () => {
    try {
      const [h, l, c, r, o, b, brs, pe] = await Promise.all([
        apiService.listHolidays(),
        apiService.listLocalities(),
        apiService.listCashConceptsRef(),
        apiService.listReferralSourcesRef(),
        apiService.listOccupationsRef(),
        apiService.listBureauProvidersRef(),
        apiService.getBranches(true),
        apiService.listPublicEntities(false, entityBranchFilter || undefined),
      ]);
      setHolidays(h);
      setLocalities(l);
      setConcepts(c);
      setReferralSources(r);
      setOccupations(o);
      setBureauProviders(b);
      setBranches(brs);
      setPublicEntities(pe);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    }
  };

  useEffect(() => { load(); }, [entityBranchFilter]);

  const toggleEntityBranch = (branchId: string) => {
    setEntityForm((f) => {
      const has = f.branchIds.includes(branchId);
      const branchIds = has ? f.branchIds.filter((id) => id !== branchId) : [...f.branchIds, branchId];
      let primaryBranchId = f.primaryBranchId;
      if (has && primaryBranchId === branchId) {
        primaryBranchId = branchIds[0] ?? '';
      } else if (!has && !primaryBranchId) {
        primaryBranchId = branchId;
      }
      return { ...f, branchIds, primaryBranchId };
    });
  };

  const emptyEntityForm = () => ({
    code: '',
    name: '',
    encoding: 'latin1',
    sendLayout: '',
    responseLayout: '',
    sendFileConfig: '',
    responseFileConfig: '',
    branchIds: [] as string[],
    primaryBranchId: '',
  });

  const parseMaybeJson = (raw: string) => (raw.trim() ? JSON.parse(raw) : undefined);

  const withBusy = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e.message);
    } finally {
      setBusy(false);
    }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'referral', label: 'Cómo se enteró' },
    { id: 'concepts', label: 'Conceptos de caja' },
    { id: 'occupations', label: 'Ocupaciones' },
    { id: 'bureau', label: 'Burós morosos' },
    { id: 'publicEntities', label: 'Entidades públicas' },
    { id: 'holidays', label: 'Feriados' },
    { id: 'localities', label: 'Localidades' },
  ];

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Datos varios</h1>
      <p className="text-gray-600 mb-6">Parametrización legacy: orígenes, caja, ocupaciones y catálogos.</p>

      {error && (
        <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
      )}

      <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-200">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === t.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'referral' && (
        <div className="space-y-6">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              withBusy(async () => {
                await apiService.createReferralSourceRef(referralForm);
                setReferralForm({ code: '', name: '' });
              });
            }}
            className="bg-white rounded-lg border p-4 grid grid-cols-1 md:grid-cols-3 gap-4"
          >
            <input required placeholder="Código (ej. WEB)" value={referralForm.code} onChange={(e) => setReferralForm({ ...referralForm, code: e.target.value })} className="px-3 py-2 border rounded-md" />
            <input required placeholder="Nombre" value={referralForm.name} onChange={(e) => setReferralForm({ ...referralForm, name: e.target.value })} className="px-3 py-2 border rounded-md" />
            <button type="submit" disabled={busy} className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50">Agregar</button>
          </form>
          <DataTable
            headers={['Código', 'Nombre', 'Estado', 'Acción']}
            rows={referralSources.map((r) => [
              r.code,
              r.name,
              r.isActive ? 'Activo' : 'Inactivo',
              r.isActive ? (
                <button key="a" onClick={() => withBusy(() => apiService.updateReferralSourceRef(r.id, { isActive: false }).then())} className="text-red-600 text-sm hover:underline">Desactivar</button>
              ) : '—',
            ])}
          />
        </div>
      )}

      {tab === 'occupations' && (
        <div className="space-y-6">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              withBusy(async () => {
                await apiService.createOccupationRef(occupationForm);
                setOccupationForm({ code: '', name: '' });
              });
            }}
            className="bg-white rounded-lg border p-4 grid grid-cols-1 md:grid-cols-3 gap-4"
          >
            <input required placeholder="Código" value={occupationForm.code} onChange={(e) => setOccupationForm({ ...occupationForm, code: e.target.value })} className="px-3 py-2 border rounded-md" />
            <input required placeholder="Nombre" value={occupationForm.name} onChange={(e) => setOccupationForm({ ...occupationForm, name: e.target.value })} className="px-3 py-2 border rounded-md" />
            <button type="submit" disabled={busy} className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50">Agregar</button>
          </form>
          <DataTable
            headers={['Código', 'Nombre', 'Estado', 'Acción']}
            rows={occupations.map((o) => [
              o.code,
              o.name,
              o.isActive ? 'Activo' : 'Inactivo',
              o.isActive ? (
                <button key="a" onClick={() => withBusy(() => apiService.updateOccupationRef(o.id, { isActive: false }).then())} className="text-red-600 text-sm hover:underline">Desactivar</button>
              ) : '—',
            ])}
          />
        </div>
      )}

      {tab === 'bureau' && (
        <div className="bg-white rounded-lg border overflow-hidden">
          <p className="p-4 text-sm text-gray-600 border-b">Proveedores de afectación por mora configurados en el sistema.</p>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Descripción</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Integración</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {bureauProviders.map((p) => (
                <tr key={p.code}>
                  <td className="px-4 py-2 text-sm font-mono">{p.code}</td>
                  <td className="px-4 py-2 text-sm font-medium">{p.name}</td>
                  <td className="px-4 py-2 text-sm text-gray-600">{p.description}</td>
                  <td className="px-4 py-2 text-center text-sm">{p.hasIntegration ? '✓' : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'publicEntities' && (
        <div className="space-y-6">
          <p className="text-sm text-gray-600">
            Convenio de descuento por entidad, vinculado a una o más sucursales (una principal).
            Layout estimado: envío DNI 8 + código 7 + importe centavos 14.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-sm text-gray-600">
              Filtrar por sucursal{' '}
              <select
                value={entityBranchFilter}
                onChange={(e) => setEntityBranchFilter(e.target.value)}
                className="ml-2 px-3 py-2 border rounded-md text-sm"
              >
                <option value="">Todas</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            </label>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              withBusy(async () => {
                if (!entityForm.branchIds.length || !entityForm.primaryBranchId) {
                  throw new Error('Asigná al menos una sucursal y marcá la principal');
                }
                const parseLayout = parseMaybeJson;
                try {
                  await apiService.createPublicEntity({
                    code: entityForm.code,
                    name: entityForm.name,
                    encoding: entityForm.encoding,
                    sendLayout: parseLayout(entityForm.sendLayout),
                    responseLayout: parseLayout(entityForm.responseLayout),
                    sendFileConfig: parseLayout(entityForm.sendFileConfig),
                    responseFileConfig: parseLayout(entityForm.responseFileConfig),
                    branchIds: entityForm.branchIds,
                    primaryBranchId: entityForm.primaryBranchId,
                  });
                } catch (err) {
                  if (err instanceof SyntaxError) throw new Error('JSON de layout/config inválido');
                  throw err;
                }
                setEntityForm(emptyEntityForm());
              });
            }}
            className="bg-white rounded-lg border p-4 grid grid-cols-1 md:grid-cols-2 gap-4"
          >
            <input required placeholder="Código (ej. EDU)" value={entityForm.code} onChange={(e) => setEntityForm({ ...entityForm, code: e.target.value })} className="px-3 py-2 border rounded-md" />
            <input required placeholder="Nombre" value={entityForm.name} onChange={(e) => setEntityForm({ ...entityForm, name: e.target.value })} className="px-3 py-2 border rounded-md" />
            <select value={entityForm.encoding} onChange={(e) => setEntityForm({ ...entityForm, encoding: e.target.value })} className="px-3 py-2 border rounded-md">
              <option value="latin1">latin1 (ISO-8859-1)</option>
              <option value="utf8">utf8</option>
            </select>
            <button
              type="button"
              className="px-3 py-2 border rounded-md text-sm"
              onClick={() => {
                apiService.publicEntityLayoutDefaults().then((d) => {
                  setEntityForm((f) => ({
                    ...f,
                    encoding: d.encoding ?? f.encoding,
                    sendLayout: JSON.stringify(d.sendLayout, null, 2),
                    responseLayout: JSON.stringify(d.responseLayout, null, 2),
                    sendFileConfig: JSON.stringify(d.sendFileConfig ?? d.sendLayout, null, 2),
                    responseFileConfig: JSON.stringify(d.responseFileConfig ?? d.responseLayout, null, 2),
                  }));
                });
              }}
            >
              Usar layout genérico (TXT + LEGAJO)
            </button>
            <div className="md:col-span-2 border rounded-md p-3 space-y-2">
              <p className="text-sm font-medium text-gray-800">Sucursales (obligatorio)</p>
              <div className="flex flex-wrap gap-3">
                {branches.map((b) => (
                  <label key={b.id} className="flex items-center gap-1.5 text-sm">
                    <input
                      type="checkbox"
                      checked={entityForm.branchIds.includes(b.id)}
                      onChange={() => toggleEntityBranch(b.id)}
                    />
                    {b.name}
                  </label>
                ))}
              </div>
              {entityForm.branchIds.length > 0 && (
                <div className="flex flex-wrap gap-3 pt-2 border-t">
                  <span className="text-xs text-gray-500 self-center">Principal:</span>
                  {entityForm.branchIds.map((id) => {
                    const b = branches.find((x) => x.id === id);
                    return (
                      <label key={id} className="flex items-center gap-1 text-sm">
                        <input
                          type="radio"
                          name="entity-primary-branch"
                          checked={entityForm.primaryBranchId === id}
                          onChange={() => setEntityForm({ ...entityForm, primaryBranchId: id })}
                        />
                        {b?.name ?? id}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            <textarea
              placeholder="sendFileConfig JSON (fileType TXT|CSV|XLSX, identifierType, identifierFormat, fields)"
              value={entityForm.sendFileConfig}
              onChange={(e) => setEntityForm({ ...entityForm, sendFileConfig: e.target.value })}
              className="md:col-span-2 px-3 py-2 border rounded-md font-mono text-xs min-h-[120px]"
            />
            <textarea
              placeholder="responseFileConfig JSON (fileType opcional hereda envío)"
              value={entityForm.responseFileConfig}
              onChange={(e) => setEntityForm({ ...entityForm, responseFileConfig: e.target.value })}
              className="md:col-span-2 px-3 py-2 border rounded-md font-mono text-xs min-h-[100px]"
            />
            <textarea
              placeholder="sendLayout legacy (opcional si hay sendFileConfig)"
              value={entityForm.sendLayout}
              onChange={(e) => setEntityForm({ ...entityForm, sendLayout: e.target.value })}
              className="px-3 py-2 border rounded-md font-mono text-xs min-h-[80px]"
            />
            <textarea
              placeholder="responseLayout legacy (opcional)"
              value={entityForm.responseLayout}
              onChange={(e) => setEntityForm({ ...entityForm, responseLayout: e.target.value })}
              className="px-3 py-2 border rounded-md font-mono text-xs min-h-[80px]"
            />
            <button type="submit" disabled={busy} className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50">Agregar entidad</button>
          </form>
          <DataTable
            headers={['Código', 'Nombre', 'Sucursales', 'Encoding', 'Estado', 'Acción']}
            rows={publicEntities.map((e) => [
              e.code,
              e.name,
              (e.branches ?? [])
                .map((x: any) => `${x.branch?.name ?? x.branchId}${x.isPrimary ? ' ★' : ''}`)
                .join(', ') || '— sin asignar —',
              e.encoding,
              e.isActive ? 'Activo' : 'Inactivo',
              <div key={e.id} className="flex gap-3">
                <button
                  className="text-blue-600 text-sm hover:underline"
                  onClick={() => {
                    setEditingEntity(editingEntity === e.id ? null : e.id);
                    setEntityForm({
                      code: e.code,
                      name: e.name,
                      encoding: e.encoding,
                      sendLayout: JSON.stringify(e.sendLayout ?? [], null, 2),
                      responseLayout: JSON.stringify(e.responseLayout ?? [], null, 2),
                      sendFileConfig: JSON.stringify(e.sendFileConfig ?? {}, null, 2),
                      responseFileConfig: JSON.stringify(e.responseFileConfig ?? {}, null, 2),
                      branchIds: e.branchIds ?? e.branches?.map((x: any) => x.branchId) ?? [],
                      primaryBranchId: e.primaryBranchId ?? e.branches?.find((x: any) => x.isPrimary)?.branchId ?? '',
                    });
                  }}
                >
                  {editingEntity === e.id ? 'Cerrar' : 'Editar'}
                </button>
                {e.isActive ? (
                  <button className="text-red-600 text-sm hover:underline" onClick={() => withBusy(() => apiService.updatePublicEntity(e.id, { isActive: false }).then())}>
                    Desactivar
                  </button>
                ) : (
                  <button className="text-green-700 text-sm hover:underline" onClick={() => withBusy(() => apiService.updatePublicEntity(e.id, { isActive: true }).then())}>
                    Activar
                  </button>
                )}
              </div>,
            ])}
          />
          {editingEntity && (
            <div className="bg-white rounded-lg border p-4 space-y-3">
              <h3 className="font-medium">Editar {entityForm.code}</h3>
              <div className="border rounded-md p-3 space-y-2">
                <p className="text-sm font-medium text-gray-800">Sucursales</p>
                <div className="flex flex-wrap gap-3">
                  {branches.map((b) => (
                    <label key={b.id} className="flex items-center gap-1.5 text-sm">
                      <input
                        type="checkbox"
                        checked={entityForm.branchIds.includes(b.id)}
                        onChange={() => toggleEntityBranch(b.id)}
                      />
                      {b.name}
                    </label>
                  ))}
                </div>
                {entityForm.branchIds.length > 0 && (
                  <div className="flex flex-wrap gap-3 pt-2 border-t">
                    <span className="text-xs text-gray-500 self-center">Principal:</span>
                    {entityForm.branchIds.map((id) => {
                      const b = branches.find((x) => x.id === id);
                      return (
                        <label key={id} className="flex items-center gap-1 text-sm">
                          <input
                            type="radio"
                            name="entity-edit-primary-branch"
                            checked={entityForm.primaryBranchId === id}
                            onChange={() => setEntityForm({ ...entityForm, primaryBranchId: id })}
                          />
                          {b?.name ?? id}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
              <textarea
                value={entityForm.sendFileConfig}
                onChange={(e) => setEntityForm({ ...entityForm, sendFileConfig: e.target.value })}
                className="w-full px-3 py-2 border rounded-md font-mono text-xs min-h-[140px]"
                placeholder="sendFileConfig"
              />
              <textarea
                value={entityForm.responseFileConfig}
                onChange={(e) => setEntityForm({ ...entityForm, responseFileConfig: e.target.value })}
                className="w-full px-3 py-2 border rounded-md font-mono text-xs min-h-[100px]"
                placeholder="responseFileConfig"
              />
              <textarea value={entityForm.sendLayout} onChange={(e) => setEntityForm({ ...entityForm, sendLayout: e.target.value })} className="w-full px-3 py-2 border rounded-md font-mono text-xs min-h-[80px]" placeholder="sendLayout legacy" />
              <textarea value={entityForm.responseLayout} onChange={(e) => setEntityForm({ ...entityForm, responseLayout: e.target.value })} className="w-full px-3 py-2 border rounded-md font-mono text-xs min-h-[80px]" placeholder="responseLayout legacy" />
              <button
                type="button"
                disabled={busy}
                className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50"
                onClick={() => withBusy(async () => {
                  if (!entityForm.branchIds.length || !entityForm.primaryBranchId) {
                    throw new Error('Asigná al menos una sucursal y marcá la principal');
                  }
                  try {
                    await apiService.updatePublicEntity(editingEntity, {
                      name: entityForm.name,
                      encoding: entityForm.encoding,
                      sendLayout: entityForm.sendLayout.trim() ? JSON.parse(entityForm.sendLayout) : undefined,
                      responseLayout: entityForm.responseLayout.trim() ? JSON.parse(entityForm.responseLayout) : undefined,
                      sendFileConfig: entityForm.sendFileConfig.trim() ? JSON.parse(entityForm.sendFileConfig) : undefined,
                      responseFileConfig: entityForm.responseFileConfig.trim() ? JSON.parse(entityForm.responseFileConfig) : undefined,
                      branchIds: entityForm.branchIds,
                      primaryBranchId: entityForm.primaryBranchId,
                    });
                    setEditingEntity(null);
                    setEntityForm(emptyEntityForm());
                  } catch (err) {
                    if (err instanceof SyntaxError) throw new Error('JSON de layout/config inválido');
                    throw err;
                  }
                })}
              >
                Guardar cambios
              </button>
            </div>
          )}
        </div>
      )}

      {tab === 'concepts' && (
        <div className="space-y-6">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              withBusy(async () => {
                await apiService.createCashConceptRef({
                  code: conceptForm.code,
                  name: conceptForm.name,
                  description: conceptForm.description || undefined,
                });
                setConceptForm({ code: '', name: '', description: '' });
              });
            }}
            className="bg-white rounded-lg border p-4 grid grid-cols-1 md:grid-cols-4 gap-4"
          >
            <input required placeholder="Código" value={conceptForm.code} onChange={(e) => setConceptForm({ ...conceptForm, code: e.target.value })} className="px-3 py-2 border rounded-md" />
            <input required placeholder="Nombre" value={conceptForm.name} onChange={(e) => setConceptForm({ ...conceptForm, name: e.target.value })} className="px-3 py-2 border rounded-md" />
            <input placeholder="Descripción" value={conceptForm.description} onChange={(e) => setConceptForm({ ...conceptForm, description: e.target.value })} className="px-3 py-2 border rounded-md" />
            <button type="submit" disabled={busy} className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50">Agregar</button>
          </form>
          <DataTable
            headers={['Código', 'Nombre', 'Descripción', 'Estado', 'Acción']}
            rows={concepts.map((c) => [
              c.code,
              c.name,
              c.description ?? '—',
              c.isActive ? 'Activo' : 'Inactivo',
              c.isActive ? (
                <button key="a" onClick={() => withBusy(() => apiService.updateCashConceptRef(c.id, { isActive: false }).then())} className="text-red-600 text-sm hover:underline">Desactivar</button>
              ) : '—',
            ])}
          />
        </div>
      )}

      {tab === 'holidays' && (
        <div className="space-y-6">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              withBusy(async () => {
                await apiService.createHoliday(holidayForm);
                setHolidayForm({ date: '', name: '' });
              });
            }}
            className="bg-white rounded-lg border p-4 grid grid-cols-1 md:grid-cols-3 gap-4"
          >
            <input required type="date" value={holidayForm.date} onChange={(e) => setHolidayForm({ ...holidayForm, date: e.target.value })} className="px-3 py-2 border rounded-md" />
            <input required value={holidayForm.name} onChange={(e) => setHolidayForm({ ...holidayForm, name: e.target.value })} className="px-3 py-2 border rounded-md" />
            <button type="submit" disabled={busy} className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50">Agregar feriado</button>
          </form>
          <DataTable
            headers={['Fecha', 'Nombre', 'Estado', 'Acción']}
            rows={holidays.map((h) => [
              String(h.date).slice(0, 10),
              h.name,
              h.isActive ? 'Activo' : 'Inactivo',
              h.isActive ? (
                <button key="a" onClick={() => withBusy(() => apiService.deactivateHoliday(h.id).then())} className="text-red-600 text-sm hover:underline">Desactivar</button>
              ) : '—',
            ])}
          />
        </div>
      )}

      {tab === 'localities' && (
        <div className="space-y-6">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              withBusy(async () => {
                await apiService.createLocality({
                  name: localityForm.name,
                  province: localityForm.province || undefined,
                  postalCode: localityForm.postalCode || undefined,
                });
                setLocalityForm({ name: '', province: '', postalCode: '' });
              });
            }}
            className="bg-white rounded-lg border p-4 grid grid-cols-1 md:grid-cols-4 gap-4"
          >
            <input required value={localityForm.name} onChange={(e) => setLocalityForm({ ...localityForm, name: e.target.value })} placeholder="Nombre" className="px-3 py-2 border rounded-md" />
            <input value={localityForm.province} onChange={(e) => setLocalityForm({ ...localityForm, province: e.target.value })} placeholder="Provincia" className="px-3 py-2 border rounded-md" />
            <input value={localityForm.postalCode} onChange={(e) => setLocalityForm({ ...localityForm, postalCode: e.target.value })} placeholder="CP" className="px-3 py-2 border rounded-md" />
            <button type="submit" disabled={busy} className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50">Agregar</button>
          </form>
          <DataTable
            headers={['Nombre', 'Provincia', 'CP', 'Acción']}
            rows={localities.map((l) => [
              l.name,
              l.province ?? '—',
              l.postalCode ?? '—',
              l.isActive ? (
                <button key="a" onClick={() => withBusy(() => apiService.deactivateLocality(l.id).then())} className="text-red-600 text-sm hover:underline">Desactivar</button>
              ) : '—',
            ])}
          />
        </div>
      )}
    </div>
  );
}

function DataTable({ headers, rows }: { headers: string[]; rows: (string | React.ReactNode)[][] }) {
  return (
    <div className="bg-white rounded-lg border overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {headers.map((h) => (
              <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-2 text-sm">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
